import type {AxiosRequestConfig} from 'axios';
import type {Schema} from 'form-render';
import {MainRequest} from '../../Services/common';
import useRequest from './useRequest';

interface Props {
    mode?: 'hybrid' | 'remote';
    url?: string;
    insert?: string;
    position?: 'before' | 'after';
    deps?: any[];
    waitFor?: string[];
    requestConfig?: AxiosRequestConfig;
    requestEnd?: (value: any) => void;
}

type AnyObject = {[key: string]: any};

export function insertPropsAfter<T extends AnyObject, K extends keyof T>(
    obj: T,
    targetKey: K,
    newProps: AnyObject,
    position: 'before' | 'after',
): T & AnyObject {
    const newObj: AnyObject = {};
    const keys = Object.keys(obj) as Array<keyof T>;

    // 遍历原对象的键值
    keys.forEach(key => {
        // 如果选择插入到目标键前方，且当前键为目标键
        if (position === 'before' && key === targetKey) {
            Object.assign(newObj, newProps);
        }

        // @ts-ignore
        newObj[key] = obj[key];

        // 如果选择插入到目标键后方，且当前键为目标键
        if (position === 'after' && key === targetKey) {
            Object.assign(newObj, newProps);
        }
    });

    return newObj as T & AnyObject;
}

export default function useSchema(
    schema: Schema,
    {
        mode = 'hybrid',
        requestConfig,
        url,
        insert,
        position = 'after',
        deps,
        waitFor,
        requestEnd,
    }: Props = {},
) {
    async function getRemoteSchema() {
        let next = true;
        if (!url) {
            next = false;
        }
        if (waitFor) {
            waitFor.forEach(item => {
                if (
                    !requestConfig?.data?.[item] &&
                    !requestConfig?.params?.[item]
                ) {
                    next = false;
                }
            });
        }
        if (!next) {
            return Promise.resolve({});
        }
        return MainRequest<SuccessData<Record<string, Schema>>>({
            ...requestConfig,
            url,
        })
            .then(res => res.data.data)
            .then(res => {
                requestEnd?.(res);
                return res;
            });
    }

    const [value, {loading}] = useRequest(getRemoteSchema, {}, deps);

    function getSchema() {
        if (mode === 'hybrid') {
            if (insert) {
                if (schema.properties && value) {
                    schema.properties = insertPropsAfter(
                        schema.properties,
                        insert,
                        value,
                        position,
                    );
                }
            } else {
                schema.properties = {
                    ...schema.properties,
                    ...value,
                };
            }
        }
        if (mode === 'remote') {
            schema.properties = {
                ...value,
            };
        }
        return schema;
    }

    return {
        loading,
        schema: getSchema(),
    };
}
